import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { MessageService } from 'primeng/api';
import { filter, map, take } from 'rxjs';

import { TranslateService } from '../../../core/i18n';
import { EstablishmentStore } from '../../establishment/store/establishment.store';
import { MissingFieldGroup, OnboardingStore } from '../store/onboarding.store';

/**
 * Resolves the guard decision based on the current state of completeness and ownership.
 *
 * - Active establishments bypass onboarding entirely.
 * - Non-owner users always pass (they see a banner instead of being blocked).
 * - Owner users with complete establishments pass.
 * - Owner users missing only contract category are redirected to /onboarding/contract.
 * - Owner users with incomplete data fields are redirected to /onboarding/complete.
 *
 * When redirecting, shows a toast message so users understand why.
 */
function resolveGuard(
  isComplete: boolean | undefined,
  isOwner: boolean,
  isEstablishmentActive: boolean,
  missingFields: MissingFieldGroup[],
  router: Router,
  messageService: MessageService,
  translateService: TranslateService,
): boolean | UrlTree {
  // Active establishments bypass onboarding entirely
  if (isEstablishmentActive) {
    return true;
  }

  // Non-owners bypass the guard — they see a "pending activation" banner on the dashboard
  if (!isOwner) {
    return true;
  }

  // Owners with complete data + signed contract pass through
  if (isComplete === true) {
    return true;
  }

  // Show toast so the user understands why they're being redirected
  messageService.add({
    severity: 'warn',
    summary: translateService.get('onboarding.completeActivationFirst'),
    life: 4000,
  });

  // Smart redirect: if only contract is missing, skip wizard and go to contract page
  const onlyContractMissing =
    missingFields.length === 1 && missingFields[0].category === 'contract';
  if (onlyContractMissing) {
    return router.createUrlTree(['/onboarding/contract']);
  }

  // Owners with incomplete data fields get redirected to the wizard
  return router.createUrlTree(['/onboarding/complete']);
}

/**
 * Guard that blocks incomplete establishments from the dashboard and all shell routes.
 *
 * Reads from:
 * - OnboardingStore.isComplete() — three-state signal (true/false/undefined)
 * - OnboardingStore.isLoading — combined loading state
 * - OnboardingStore.missingFields() — grouped missing fields by category
 * - EstablishmentStore.isOwner() — role check
 * - EstablishmentStore.isEstablishmentActive() — active status bypass
 *
 * Behavior:
 * - Active establishments bypass the guard entirely
 * - Fast-path: If not loading, evaluates immediately
 * - Async path: If loading, returns Observable that waits for loading to resolve
 * - Non-owner users always pass through (GATE-05)
 * - Owner users must have isComplete === true to pass (GATE-01, GATE-02)
 * - Smart redirect: contract-only missing -> /onboarding/contract (BYPS-03)
 * - Shows a toast message before redirecting so users understand why
 */
export const onboardingGuard: CanActivateFn = () => {
  const onboardingStore = inject(OnboardingStore);
  const establishmentStore = inject(EstablishmentStore);
  const router = inject(Router);
  const messageService = inject(MessageService);
  const translateService = inject(TranslateService);

  // Fast-path: if not loading, check immediately
  if (!onboardingStore.isLoading()) {
    return resolveGuard(
      onboardingStore.isComplete(),
      establishmentStore.isOwner(),
      establishmentStore.isEstablishmentActive(),
      onboardingStore.missingFields(),
      router,
      messageService,
      translateService,
    );
  }

  // Async path: wait for loading to complete, then evaluate
  return toObservable(onboardingStore.isLoading).pipe(
    filter((loading) => !loading),
    take(1),
    map(() =>
      resolveGuard(
        onboardingStore.isComplete(),
        establishmentStore.isOwner(),
        establishmentStore.isEstablishmentActive(),
        onboardingStore.missingFields(),
        router,
        messageService,
        translateService,
      ),
    ),
  );
};
